import { formatPersonName } from "@/lib/formatPersonName";
import { BOOKING_STATE_LABELS, type BookingState } from "@/types/booking";

/**
 * Breakdown dimensions whose stored values are often snake_case identifiers
 * (`john_doe`, `pending_book`). Display as Title Case / canonical labels.
 */
const FORMATTED_DIMENSIONS = new Set([
  "agent",
  "sales_person",
  "sales",
  "opened_by",
  "consignee",
  "customer",
  "end_client",
  "shipper",
  "state",
]);

function dimensionKey(key: string | null | undefined): string | null {
  if (!key) return null;
  return key.endsWith("_not") ? key.slice(0, -4) : key;
}

/** True when `key` is a formatted dimension (strips a trailing `_not`). */
export function isPartyDimension(key: string | null | undefined): boolean {
  const normalized = dimensionKey(key);
  return normalized != null && FORMATTED_DIMENSIONS.has(normalized);
}

function formatStateValue(value: string): string {
  const known = BOOKING_STATE_LABELS[value as BookingState];
  if (known) return known;
  return formatPersonName(value);
}

/**
 * Humanize a dimension value. Non-formatted keys and reserved bucket labels
 * (`Other`, `Unknown`) are returned unchanged so chart domains stay stable.
 */
export function formatDimensionValue(
  key: string | null | undefined,
  value: string | null | undefined,
): string {
  if (value == null) return "";
  if (!isPartyDimension(key)) return value;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "Other" || trimmed === "Unknown") return value;
  if (dimensionKey(key) === "state") return formatStateValue(trimmed);
  return formatPersonName(trimmed);
}
